import { useState } from 'react';
import { FileImage, Image as ImageIcon, Plus, Loader2, Upload } from 'lucide-react';
import { useHaptic } from '../hooks/useHaptic';
import { stampsAPI } from '../services/api';
import './StampSelector.css';

function StampSelector({ stamps, selectedStamp, onSelectStamp, onStampsUpdate }) {
  const [activeTab, setActiveTab] = useState('signature');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const haptic = useHaptic();
  
  const signatures = stamps.filter(s => s.type === 'signature');
  const stampsList = stamps.filter(s => s.type === 'stamp');
  
  const currentStamps = activeTab === 'signature' ? signatures : stampsList;

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    haptic.buttonPress();

    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      haptic.warning();
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('Пожалуйста, выберите изображение');
      } else {
        alert('Пожалуйста, выберите изображение');
      }
      return;
    }

    // Проверка размера файла (10MB)
    if (file.size > 10 * 1024 * 1024) {
      haptic.warning();
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('Размер файла не должен превышать 10MB');
      } else {
        alert('Размер файла не должен превышать 10MB');
      }
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Загружаем изображение...');

    try {
      haptic.fileUpload();
      
      // Симулируем прогресс загрузки с разными статусами (более плавно)
      const statuses = [
        { progress: 15, message: 'Загружаем изображение...', delay: 600 },
        { progress: 35, message: 'Обрабатываем через AI...', delay: 800 },
        { progress: 55, message: 'Удаляем фон...', delay: 700 },
        { progress: 75, message: 'Оптимизируем качество...', delay: 600 },
        { progress: 88, message: 'Почти готово...', delay: 500 }
      ];
      
      let currentStatusIndex = 0;
      
      const updateStatus = () => {
        if (currentStatusIndex < statuses.length) {
          const status = statuses[currentStatusIndex];
          setUploadProgress(status.progress);
          setUploadStatus(status.message);
          currentStatusIndex++;
          
          if (currentStatusIndex < statuses.length) {
            setTimeout(updateStatus, statuses[currentStatusIndex - 1].delay);
          }
        }
      };
      
      // Начинаем обновление статусов
      setTimeout(updateStatus, statuses[0].delay);

      const response = await stampsAPI.upload(file, activeTab);
      
      setUploadProgress(100);
      setUploadStatus('Файл успешно обработан!');
      
      // Даем время показать 100% прогресс
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Предзагружаем medium версию для быстрой вставки в документ
      if (response.data && response.data.stamp) {
        const stampId = response.data.stamp.id;
        const mediumImageUrl = `${import.meta.env.VITE_API_URL}/stamps/${stampId}/medium?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`;
        
        // Создаем Image объект для предзагрузки
        const preloadImg = new Image();
        preloadImg.src = mediumImageUrl;
        
        console.log(`🔄 Предзагрузка medium версии для печати ${stampId}`);
      }
      
      haptic.success();
      // Показываем уведомление об успехе
      if (window.Telegram?.WebApp?.showPopup) {
        window.Telegram.WebApp.showPopup({
          title: "Готово",
          message: `${activeTab === 'signature' ? 'Подпись' : 'Печать'} успешно загружена!`,
          buttons: [{ type: "ok" }]
        });
      }

      // Обновляем список печатей в родительском компоненте
      if (onStampsUpdate) {
        await onStampsUpdate();
      }
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      haptic.error();
      
      const errorMessage = error.response?.data?.error || 'Не удалось загрузить изображение';
      
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      // Сбрасываем input
      event.target.value = '';
    }
  };

  return (
    <div className="stamp-selector">
      <h3>Выберите печать или подпись</h3>
      
      {/* Табы */}
      <div className="selector-tabs">
        <button
          className={`selector-tab ${activeTab === 'signature' ? 'active' : ''}`}
          onClick={() => {
            haptic.toggle();
            setActiveTab('signature');
          }}
        >
          <ImageIcon size={16} />
          Подписи ({signatures.length})
        </button>
        <button
          className={`selector-tab ${activeTab === 'stamp' ? 'active' : ''}`}
          onClick={() => {
            haptic.toggle();
            setActiveTab('stamp');
          }}
        >
          <ImageIcon size={16} />
          Печати ({stampsList.length})
        </button>
      </div>

      {/* Кнопка загрузки */}
      <div className="upload-section-selector">
        <input
          type="file"
          id="selector-file-upload"
          accept="image/*"
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <label htmlFor="selector-file-upload" className={`upload-button-selector ${uploading ? 'disabled' : ''}`}>
          {uploading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>{uploadStatus}</span>
            </>
          ) : (
            <>
              <Plus size={18} />
              <span>Добавить {activeTab === 'signature' ? 'подпись' : 'печать'}</span>
            </>
          )}
        </label>
        
        {/* Прогресс-бар */}
        {uploading && (
          <div className="upload-progress-container">
            <div 
              className="upload-progress-bar" 
              style={{ width: `${uploadProgress}%` }}
            >
              <div className="upload-progress-shimmer"></div>
            </div>
          </div>
        )}
      </div>

      {/* Подсказка */}
      {currentStamps.length === 0 && (
        <div className="info-box-selector">
          <Upload size={16} />
          <span>Нажмите кнопку выше, чтобы добавить {activeTab === 'signature' ? 'подпись' : 'печать'}</span>
        </div>
      )}
      
      {currentStamps.length === 0 ? (
        <div className="empty-stamps">
          <FileImage size={48} className="empty-icon" />
          <p>Нет {activeTab === 'signature' ? 'подписей' : 'печатей'}</p>
          <p>Добавьте {activeTab === 'signature' ? 'подпись' : 'печать'}, используя кнопку выше</p>
        </div>
      ) : (
        <div className="stamps-grid">
          {currentStamps.map((stamp) => (
            <div
              key={stamp.id}
              className={`stamp-item ${selectedStamp?.id === stamp.id ? 'selected' : ''}`}
              onClick={() => onSelectStamp(stamp)}
            >
              <div className="stamp-image-wrapper">
                <img
                  src={`${import.meta.env.VITE_API_URL}/stamps/${stamp.id}/thumbnail?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`}
                  alt={stamp.type}
                  className="stamp-image"
                  loading="lazy"
                />
              </div>
              <div className="stamp-info">
                <span className="stamp-name">{stamp.original_name || 'Без названия'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StampSelector;
